import { useMemo } from "react";
import { matchPath, Navigate, useLocation, useParams } from "react-router-dom";
import { isOldVmRetired, isOperatorCreatedAfterMigration } from "@/constants/vehicleMigration";
import { useOperator } from "@/hooks";
import { Container } from "@/components/atoms";
import { Tabs } from "@/components/molecules";
import { PageHeader } from "@/components/organisms";
import { AffiliateVehiclesActions } from "@/components/organisms/Vehicles/AffiliateVehicles/AffiliateVehiclesActions";
import { AffiliateVehiclesActionsMenu } from "@/components/organisms/Vehicles/AffiliateVehicles/AffiliateVehiclesActionsMenu";
import { ArchivedVehiclesActionMenu } from "@/components/organisms/Vehicles/ArchivedVehicles/ArchivedVehiclesActionMenu";
import { ArchivedVehiclesActions } from "@/components/organisms/Vehicles/ArchivedVehicles/ArchivedVehiclesActions";
import { LegacyMyVehiclesActionMenu } from "@/components/organisms/Vehicles/LegacyMyVehicles/LegacyMyVehiclesActionMenu";
import { LegacyMyVehiclesActions } from "@/components/organisms/Vehicles/LegacyMyVehicles/LegacyMyVehiclesActions";
import { VehicleFiltersProvider } from "@/components/organisms/Vehicles/features/Filters/useVehicleFilters";
import { useGetVehicleQuery } from "@/redux/apis/vehicle/vehicleApi";
import { RequireAccess } from "@/routes/RequireAccess/RequireAccess";
import { LegacyArchivedVehicles } from "./LegacyArchivedVehicles";
import { LegacyAffiliateVehicles } from "./LegacyAffiliateVehicles";
import { LegacyMyVehicles } from "./LegacyMyVehicles";

export const LegacyVehicles = () => {
  const { pathname } = useLocation();
  const { joined } = useOperator();

  if (isOldVmRetired() || isOperatorCreatedAfterMigration(joined)) return <Navigate to="../vehicles" replace />;

  const pageTabs = [
    {
      name: "My Vehicles",
      to: "my-vehicles",
    },
    {
      name: "My Drivers' Vehicles",
      to: "affiliate-vehicles",
    },
    {
      name: "Archived Vehicles",
      to: "archived-vehicles",
    },
  ];

  const isParentPage = Boolean(
    matchPath({ path: "/vehicles-legacy/:slug" }, pathname) ||
      matchPath({ path: "operator/:operatorId/vehicles-legacy/:slug" }, pathname)
  );

  return (
    <VehicleFiltersProvider>
      <Container className="w-full">
        <div className="sticky top-0 z-30 w-full bg-white">
          <CustomHeader />
          {isParentPage && (
            <div className="flex w-full gap-2 border-b !border-neutral-mid-gray py-1 max-xl:justify-between lg:py-0">
              <Tabs className="no-scrollbar min-w-0 border-none" tabs={pageTabs} />
              <ActionMenu />
            </div>
          )}
        </div>
        <Actions />
        <RequireAccess action="view" subject="providerConfigVehicle" />
      </Container>
    </VehicleFiltersProvider>
  );
};

const ActionMenu = () => {
  const { pathname } = useLocation();

  switch (true) {
    case pathname.includes("/vehicles-legacy/my-vehicles"):
      return <LegacyMyVehiclesActionMenu />;
    case pathname.includes("/vehicles-legacy/archived-vehicles"):
      return <ArchivedVehiclesActionMenu />;
    case pathname.includes("/vehicles-legacy/affiliate-vehicles"):
      return <AffiliateVehiclesActionsMenu />;
    default:
      return null;
  }
};

const Actions = () => {
  const { pathname } = useLocation();

  switch (true) {
    case pathname.includes("/vehicles-legacy/my-vehicles"):
      return <LegacyMyVehiclesActions />;
    case pathname.includes("/vehicles-legacy/archived-vehicles"):
      return <ArchivedVehiclesActions />;
    case pathname.includes("/vehicles-legacy/affiliate-vehicles"):
      return <AffiliateVehiclesActions />;
    default:
      return null;
  }
};

const CustomHeader = () => {
  const { vehicleId } = useParams<{ vehicleId: string }>();
  const { data } = useGetVehicleQuery(vehicleId || "", { skip: !vehicleId });

  const moreCrumbs = useMemo(() => {
    if (!vehicleId || !data) return undefined;
    return [{ name: `${data.model.make} ${data.model.model} (${data.registrationNumber})`, to: "#", current: true }];
  }, [data, vehicleId]);

  return <PageHeader title="Vehicle" skipCrumbs={1} moreCrumbs={moreCrumbs} />;
};

LegacyVehicles.MyVehicles = LegacyMyVehicles;
LegacyVehicles.AffiliateVehicles = LegacyAffiliateVehicles;
LegacyVehicles.ArchivedVehicles = LegacyArchivedVehicles;
